import { memo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getFormattedDate } from 'lib/common';
import { Alert, Button, ConfirmButton } from 'components/common';
import { Box } from 'components/common/box';
import { useEditorDispatch } from 'components/editor';

import type { Editor } from 'types';

interface Props {
  currentChapter: Editor['editorCurrentChapter'];
  previewType: Editor['editorPreviewType'];
  previewVersion: Editor['editorPreviewVersion'];
  showTransferBlocksWarning: boolean;
}

const EditorVersionPreviewInfo = ({
  currentChapter,
  previewType,
  previewVersion,
  showTransferBlocksWarning
}: Props) => {
  const editorDispatch = useEditorDispatch();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const router = useRouter();

  const handlePreviewClose = async () => {
    let url = `/admin/courses/${course.slug}/edit/${currentChapter.slug}`;

    if (previewType === 'course') {
      const response = await api.get(`admin/courses/${course.id}/chapters`);
      const firstChapterSlug = response.data.chapters?.[0]?.slug;
      url = `/admin/courses/${course.slug}/edit/${firstChapterSlug}`;
    }

    editorDispatch({
      type: 'updateContext',
      update: {
        editorActiveView:
          previewType === 'course'
            ? 'course-version-history'
            : 'chapter-version-history',
        editorActiveLanguageVersion: null,
        editorTranslationReferenceLanguage: course.language
      }
    });

    router.push(url, url, { shallow: true });
  };

  const handleRollback = async () => {
    try {
      const urlRoot = `/admin/courses/${course.id}`;

      const values =
        previewType === 'course'
          ? {
              message: messages.coursesRollbackSuccess,
              url: `${urlRoot}/versions/${previewVersion}/rollback-course`
            }
          : {
              message: messages.coursesRollbackChapterSuccess,
              url: `${urlRoot}/versions/${previewVersion}/rollback-chapter/${currentChapter.id}`
            };

      await api.put(values.url);
      dispatch.addNotice({ text: values.message, type: 'success' });

      editorDispatch({
        type: 'updateContext',
        update: {
          editorActiveLanguageVersion: undefined,
          editorActiveView: 'edit',
          editorIsFetching: true
        }
      });

      if (previewType === 'course') {
        // this removes chapter and preview version data from url
        router.push(`/admin/courses/${course.slug}/edit`);
      } else {
        router.push(
          `/admin/courses/${course.slug}/edit/${currentChapter.slug}`
        );
      }

      return true;
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
      return false;
    }
  };

  return (
    <Box style={{ marginBottom: 20 }}>
      <div className="content">
        <Alert
          size="medium"
          text={getMessageWithPlaceholders({
            message:
              previewType === 'course'
                ? messages.coursePreviewingVersion
                : messages.coursePreviewingVersionChapter,
            placeholders: {
              date: getFormattedDate({
                date: previewVersion,
                dateFormat: 'dd.MM.yyyy HH:mm:ss'
              })
            }
          })}
          type="warning-light"
        />

        {showTransferBlocksWarning ? (
          <Alert
            size="small"
            style={{ marginTop: 10 }}
            text={messages.coursesRollbackTransferredBlocksWarning}
            type="error-light"
          />
        ) : null}

        <div className="actions">
          <Button
            category="light"
            icon="times"
            onClick={handlePreviewClose}
            size="small"
            text={messages.courseClosePreview}
          />

          <ConfirmButton
            actionText={messages.commonConfirm}
            buttonIcon="history"
            buttonSize="small"
            buttonText={messages.courseRevertToThisVersion}
            confirmText={messages.courseRevertConfirm}
            confirmTitle={
              previewType === 'course'
                ? messages.courseRevertCourseVersion
                : messages.courseRevertChapterVersion
            }
            onConfirm={handleRollback}
          />
        </div>
      </div>

      <style jsx>{`
        .actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 15px;
        }
      `}</style>
    </Box>
  );
};

export default memo(EditorVersionPreviewInfo);
